import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Sparkles, TrendingUp, ClipboardList, ArrowRight } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import { api } from '../api.js';
import { useAuthStore } from '../store.js';
import { Card, CardHeader, ScoreBadge, Badge, Button, EmptyState, PageHeader, Spinner } from '../components/ui.jsx';

export default function IndividualDashboard({ fromAdmin }) {
  const { userId: paramUserId } = useParams();
  const me = useAuthStore(s => s.user);
  const userId = paramUserId || me?.id;

  const { data, isLoading } = useQuery({
    queryKey: ['individual-insights', userId],
    queryFn: () => api.individualInsights(userId),
    enabled: !!userId
  });

  if (isLoading || !data) return <div className="min-h-[40vh] grid place-items-center"><Spinner className="w-8 h-8 text-primary-600" /></div>;

  const completed = data.completed_modules || [];
  const pending = data.pending_assignments || [];

  // Aggregate radar across all modules — average per competency
  const radarData = aggregateRadar(completed);
  const overallAvg = completed.length > 0
    ? Math.round(completed.reduce((a, c) => a + (c.overall_score || 0), 0) / completed.length)
    : 0;

  return (
    <>
      <PageHeader
        title={fromAdmin ? `${data.user.full_name}` : 'Your dashboard'}
        description={fromAdmin ? data.user.job_title || data.user.email : 'Where you stand and what to focus on next.'} />

      {completed.length === 0 ? (
        <EmptyState icon={ClipboardList}
          title="No assessments completed yet"
          description="Complete your first assessment to see your capability profile here."
          action={<Link to="/assessments"><Button>Browse assessments</Button></Link>} />
      ) : (
        <>
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <Card className="text-center">
              <div className="text-xs uppercase tracking-wider font-semibold text-ink-500 mb-3">Overall capability</div>
              <ScoreBadge score={overallAvg} large />
              <p className="text-sm text-ink-500 mt-3">Across {completed.length} module{completed.length === 1 ? '' : 's'}</p>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader title="Capability map" subtitle="Average score across all completed modules" />
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius={86}>
                    <PolarGrid stroke="#E2E8F0" />
                    <PolarAngleAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#94A3B8' }} />
                    <Radar dataKey="score" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.18} strokeWidth={2} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <Card>
              <CardHeader title="Top strengths" />
              <ul className="space-y-3">
                {(data.strengths || []).slice(0, 5).map((s, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <ScoreBadge score={s.score} />
                    <div className="flex-1">
                      <div className="font-medium text-ink-900">{s.label}</div>
                      <div className="text-xs text-ink-500">{s.module_name}</div>
                    </div>
                    <Sparkles className="w-4 h-4 text-accent-500" />
                  </li>
                ))}
              </ul>
            </Card>
            <Card>
              <CardHeader title="Development priorities" />
              <ul className="space-y-3">
                {(data.development_priorities || []).slice(0, 5).map((s, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <ScoreBadge score={s.score} />
                    <div className="flex-1">
                      <div className="font-medium text-ink-900">{s.label}</div>
                      <div className="text-xs text-ink-500">{s.module_name}</div>
                    </div>
                    <TrendingUp className="w-4 h-4 text-primary-500" />
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {pending.length > 0 ? (
            <Card>
              <CardHeader title="Assigned to you" subtitle={`${pending.length} module${pending.length === 1 ? '' : 's'} pending`} />
              <div className="space-y-2">
                {pending.map(p => (
                  <Link key={p.id} to="/assessments"
                    className="flex items-center gap-3 p-3 rounded-lg border border-ink-200 hover:border-primary-300 hover:bg-primary-50/40 transition-all">
                    <Badge variant="blue">Pending</Badge>
                    <div className="flex-1">
                      <div className="font-medium text-ink-900">{p.name}</div>
                      <div className="text-xs text-ink-500">{p.due_at ? `Due ${new Date(p.due_at).toLocaleDateString('en-AU')}` : 'No due date'}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-ink-400" />
                  </Link>
                ))}
              </div>
            </Card>
          ) : null}

          <Card className="mt-6">
            <CardHeader title="Completed modules" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {completed.map(c => (
                <Link key={c.session_id} to={fromAdmin ? `/insights/individuals/${userId}` : `/results/${c.session_id}`}
                  className="block p-4 rounded-lg border border-ink-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <ScoreBadge score={c.overall_score || 0} />
                    {c.benchmark_percentile != null ? <span className="text-xs text-ink-500">P{c.benchmark_percentile}</span> : null}
                  </div>
                  <div className="font-semibold text-ink-900 text-sm leading-tight mb-1">{c.module.name}</div>
                  <div className="text-xs text-ink-500">{new Date(c.completed_at).toLocaleDateString('en-AU')}</div>
                </Link>
              ))}
            </div>
          </Card>
        </>
      )}
    </>
  );
}

function aggregateRadar(modules) {
  const buckets = new Map();
  for (const m of modules) {
    const defs = m.module.competencies || [];
    for (const [key, v] of Object.entries(m.competency_scores || {})) {
      const label = (defs.find(d => d.key === key) || {}).label || key;
      if (!buckets.has(key)) buckets.set(key, { label, scores: [] });
      buckets.get(key).scores.push(v.score);
    }
  }
  return [...buckets.values()].map(b => ({
    label: b.label,
    score: Math.round(b.scores.reduce((a, c) => a + c, 0) / b.scores.length)
  }));
}
